import React, { useRef, useEffect } from 'react';
import { getPlotPalette, onThemeChange } from '../theme/themeColors';

function ConfusionMatrixPlot() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // DPI-aware sizing
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        const w = rect.width;
        const h = rect.height;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';
        const g = canvas.getContext('2d');
        g.scale(dpr, dpr);

        const classes = ['Normal', 'Bacterial', 'Viral', 'Other'];
        const n = classes.length;
        // Fixed counts (pulsing slightly for animation)
        const counts = [
            [45, 2, 3, 0],
            [3, 38, 2, 2],
            [2, 1, 42, 0],
            [1, 0, 1, 48],
        ];
        const topPad = 28, leftPad = 34, rightPad = 30, bottomPad = 22;
        const gridW = w - leftPad - rightPad;
        const gridH = h - topPad - bottomPad;
        const cellW = gridW / n;
        const cellH = gridH / n;
        let animFrame;
        let p = getPlotPalette();
        const offThemeChange = onThemeChange(() => {
            p = getPlotPalette();
        });

        function drawCM() {
            const t = performance.now() * 0.001;
            g.clearRect(0, 0, w, h);

            // Column headers ("Predicted")
            g.fillStyle = `rgba(${p.axis}, 0.5)`;
            g.font = 'bold 8px Inter, sans-serif';
            g.textAlign = 'center';
            g.textBaseline = 'middle';
            g.fillText('Predicted \u2192', leftPad + gridW / 2, 10);
            for (let c = 0; c < n; c++) {
                g.fillStyle = `rgba(${p.s2}, 0.7)`;
                g.font = 'bold 9px Inter, sans-serif';
                g.fillText(classes[c], leftPad + c * cellW + cellW / 2, topPad - 10);
            }

            // Row labels ("Actual")
            for (let r = 0; r < n; r++) {
                g.fillStyle = `rgba(${p.s2}, 0.7)`;
                g.font = 'bold 9px Inter, sans-serif';
                g.textAlign = 'right';
                g.textBaseline = 'middle';
                g.fillText(classes[r], leftPad - 6, topPad + r * cellH + cellH / 2);
            }

            // Row & column totals
            const rowSums = counts.map(row => row.reduce((a, b) => a + b, 0));
            const colSums = [];
            for (let c = 0; c < n; c++) {
                let sum = 0;
                for (let r = 0; r < n; r++) sum += counts[r][c];
                colSums.push(sum);
            }
            const total = rowSums.reduce((a, b) => a + b, 0);

            // Cells
            for (let r = 0; r < n; r++) {
                for (let c = 0; c < n; c++) {
                    const isDiag = (r === c);
                    const count = counts[r][c];
                    const pulse = 1 + 0.06 * Math.sin(t * 0.6 + r * 0.8 + c);
                    const maxVal = isDiag ? 50 : 10;
                    const alpha = 0.25 + (count / maxVal) * 0.5 * pulse;
                    g.fillStyle = isDiag
                        ? `rgba(${p.good}, ${Math.min(0.85, alpha)})`
                        : `rgba(${p.s1}, ${Math.min(0.5, alpha * 0.5)})`;
                    g.fillRect(leftPad + c * cellW + 1, topPad + r * cellH + 1, cellW - 2, cellH - 2);
                    // Number
                    g.fillStyle = `rgba(${p.cellText}, 0.8)`;
                    g.font = 'bold 10px Inter, sans-serif';
                    g.textAlign = 'center';
                    g.textBaseline = 'middle';
                    g.fillText(count, leftPad + c * cellW + cellW / 2, topPad + r * cellH + cellH / 2);
                }
            }

            // Row totals
            const totColX = leftPad + gridW + 8;
            g.fillStyle = `rgba(${p.axis}, 0.5)`;
            g.font = 'bold 8px Inter, sans-serif';
            g.textAlign = 'center';
            g.fillText('\u03A3', totColX, topPad - 10);
            for (let r = 0; r < n; r++) {
                g.fillStyle = `rgba(${p.axis}, 0.45)`;
                g.font = 'bold 9px Inter, sans-serif';
                g.textAlign = 'center';
                g.textBaseline = 'middle';
                g.fillText(rowSums[r], totColX, topPad + r * cellH + cellH / 2);
            }

            // Column totals
            const totRowY = topPad + gridH + 10;
            for (let c = 0; c < n; c++) {
                g.fillStyle = `rgba(${p.axis}, 0.45)`;
                g.font = 'bold 9px Inter, sans-serif';
                g.textAlign = 'center';
                g.textBaseline = 'middle';
                g.fillText(colSums[c], leftPad + c * cellW + cellW / 2, totRowY);
            }
            // Grand total
            g.fillStyle = `rgba(${p.s2}, 0.65)`;
            g.font = 'bold 10px Inter, sans-serif';
            g.fillText(total, totColX, totRowY);

            animFrame = requestAnimationFrame(drawCM);
        }

        animFrame = requestAnimationFrame(drawCM);

        return () => {
            offThemeChange();
            if (animFrame) cancelAnimationFrame(animFrame);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                width: '100%',
                marginTop: '16px',
                borderRadius: '8px',
                border: '1px solid var(--plot-border)',
                display: 'block',
                background: 'var(--plot-bg)',
            }}
            aria-label="Confusion matrix for chest X-ray classification"
        />
    );
}

export default ConfusionMatrixPlot;